/**
 * Synergy panel view-model (TRD-09 §8).
 *
 * The row order, the pip states and the tier lighting are the decisions; the JSX is a
 * projection. Kept free of React so they are testable — a panel that lights tier 4 at
 * count 3 would mis-teach the whole synergy system, and that is a logic bug, not a
 * styling bug.
 */
import type { SynergyStatus } from '@prism/sim';

/** The three thresholds, marked on the pip bar (R-09-16). */
export const THRESHOLDS = [2, 4, 6] as const;

export interface PipState {
  filled: boolean;
  /** This pip sits on a 2/4/6 boundary and gets the threshold marker. */
  isThreshold: boolean;
  /** A filled threshold pip whose tier is active — lit in the class colour. */
  active: boolean;
}

/**
 * The six pips of one row (R-09-16).
 *
 * `filled` tracks the raw count; `active` lights a threshold pip only when the tier is
 * actually reached. The two can disagree — five towers fill five pips but light only the
 * 2 and 4 markers — and that disagreement is the display: "you are one short of 6".
 */
export function pipsFor(status: SynergyStatus): PipState[] {
  const pips: PipState[] = [];
  for (let i = 1; i <= 6; i++) {
    const isThreshold = i === 2 || i === 4 || i === 6;
    pips.push({
      filled: i <= status.count,
      isThreshold,
      active: isThreshold && status.activeTier >= i,
    });
  }
  return pips;
}

/** `4 / 6` while a next threshold exists, plain `6` at the cap (R-09-16). */
export function countLabel(status: SynergyStatus): string {
  return status.nextThreshold === null
    ? String(status.count)
    : `${status.count} / ${status.nextThreshold}`;
}

export interface TierLine {
  threshold: 2 | 4 | 6;
  summary: string;
  /** Lit when reached; dimmed with the threshold shown otherwise (R-09-18). */
  active: boolean;
}

export function tierLines(
  tiers: readonly { threshold: 2 | 4 | 6; summary: string }[],
  activeTier: 0 | 2 | 4 | 6,
): TierLine[] {
  return tiers.map((t) => ({
    threshold: t.threshold,
    summary: t.summary,
    active: activeTier >= t.threshold,
  }));
}

/**
 * Class ids with no presence on the board, for the "not active" expander (R-09-19).
 *
 * Preserves the order of `allClasses` (the authored content order), so the collapsed
 * list is stable however the board changes.
 */
export function inactiveClasses<T extends string>(
  allClasses: readonly T[],
  statuses: readonly SynergyStatus[],
): T[] {
  // The sim reports every class in the table, count 0 included — presence in the list
  // is not presence on the board.
  const active = new Set<string>(statuses.filter((s) => s.count > 0).map((s) => s.classId));
  return allClasses.filter((id) => !active.has(id));
}
